import { hasRealmRole } from './authUtils';

// The single source of truth for who may reach the channel-settlement money surfaces: the org-wide
// Ledger Reconciliation page and the per-order Settlement tab it deep-links into. The sidebar entry,
// both page guards, and the reconciliation fetch suppression all read this predicate, so a visible
// nav entry always links to a surface the viewer can open and a visible row always deep-links to a
// tab that renders — the same rule SUPER_ADMIN_SURFACES enforces for the admin menu.
//
// Two independent grants, OR'd:
//  - Acenda staff roles: super users / management see settlement surfaces in EVERY organization.
//  - The `settlements` organization tag: every member of a tagged organization sees them in that
//    organization only. Operators grant it from Organizations Admin -> Details -> Quick selectors
//    -> Enable Settlements, which writes the tag onto the organization record.
//
// 'playwright_test_user' is deliberately NOT a grant here, unlike the SUPER_ADMIN_SURFACES entries.
// Under the previous role-only gate it was the only way an e2e spec could reach these surfaces, so
// every spec passed and the denial path was untestable. The seeded account now reaches them through
// a mocked `settlements` tag instead, which makes BOTH polarities expressible; re-adding the role
// would make the denial specs pass for the wrong reason. The role arm is covered by unit tests.
export const SETTLEMENTS_TAG = 'settlements';
export const SETTLEMENTS_ROLES = ['superuser', 'acenda_the_management'] as const;

// OrganizationContext.hasTag: compares case-insensitively, and returns false while the organization
// membership that carries `tags` is still loading — "not yet", not "no". Callers that DENY on a
// false result must distinguish the two; useSettlementsAccess does that for them.
export type HasOrganizationTag = (tagName: string) => boolean;

export const settlementsRoles = (): string[] => [...SETTLEMENTS_ROLES];

// The role half is a module read of the committed session snapshot (non-reactive); the tag half is
// a React context callback (reactive). Do not wire these two seams together by hand at a call site
// — use useSettlementsAccess (src/hooks/useSettlementsAccess.ts), which owns the identity-generation
// subscription and the "not yet decided" state.
export const canAccessSettlements = (hasTag: HasOrganizationTag): boolean => hasRealmRole(settlementsRoles()) || hasTag(SETTLEMENTS_TAG);
